<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import Badge from '../Badge';
import dateMixin from '../../mixins/dateMixin';

export default {
  name: 'XmlChannelJobList',
  components: {
    Badge,
  },
  mixins: [
    dateMixin,
  ],
  props: {
    show: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      dialog: false,
      headers: [
        { text: 'Status', value: 'badge', sortable: false },
        { text: 'Start time', value: 'runStart', sortable: false },
        { text: 'End time', value: 'runEnd', sortable: false },
        { text: 'Runtime', value: 'runTime', sortable: false },
        { text: 'Rows', value: 'rows', sortable: false },
        { text: 'Messages', value: 'messages', sortable: false },
      ],
    };
  },
  computed: {
    ...mapState({
      xmlJobList: state => state.xmlChannelStatus.xmlJobList,
    }),
    ...mapGetters({
      badges: 'xmlChannelStatus/getBadges',
    }),
  },
  methods: {
    ...mapActions({
      getXmlJobs: 'xmlChannelStatus/getXmlJobs',
    }),
    refreshLobList() {
      if (this.xmlJobList.loading) {
        return;
      }
      this.getXmlJobs();
    },
    /**
     * What the job logged (why it failed or was not set live) and what each export
     * said (a sync error), as one list.
     */
    getMessages(job) {
      const logs = (job.logs || []).map(log => ({
        key: `log-${log.id}`,
        label: log.statusCode === 0 ? 'Import error' : 'Import warning',
        error: log.statusCode === 0,
        time: log.createdAt,
        message: log.message,
        detail: log.detail,
      }));
      const exports = (job.exports || [])
        .filter(xmlExport => xmlExport.message)
        .map(xmlExport => ({
          key: `export-${xmlExport.id}`,
          label: `Export to ${xmlExport.system}`,
          error: xmlExport.status === 'SYNC_ERROR',
          time: xmlExport.startTime,
          message: xmlExport.message,
          detail: '',
        }));
      return logs.concat(exports);
    },
  },
  watch: {
    show() {
      this.dialog = true;
    },
  },
};
</script>

<template>
  <v-dialog v-model="dialog" width="1000" scrollable>
    <v-flex v-if="xmlJobList.xmlChannel">
      <template v-slot:activator="{ on }">
        <v-btn
          v-on="on"
          :loading="xmlJobList.loading"
          color="primary"
          outline
        >
          {{ $t('xmlChannelStatus.xmlChannelJobList.title') }}
        </v-btn>
      </template>
      <v-card>
        <v-card-title
          class="headline"
          primary-title
        >
          <span class="d-inline-block">
            {{ xmlJobList.xmlChannel.webservice }} -
            {{ xmlJobList.xmlChannel.system }} |
            {{ $t('xmlChannelStatus.xmlChannelJobList.title') }}
          </span>
          <v-btn
            @click="refreshLobList"
            :disabled="xmlJobList.loading"
            icon
          >
            <v-icon :class="{ 'icon-spin': xmlJobList.loading }">refresh</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="headers"
            :items="xmlJobList.items"
            :loading="xmlJobList.loading"
            item-key="id"
            class="elevation-1"
            hide-actions
          >
            <template v-slot:no-data>
              <tr>
                <td v-if="!xmlJobList.loading" colspan="6" class="text-xs-center">
                  {{ $t('xmlChannelStatus.xmlChannelJobList.noContent') }}
                </td>
              </tr>
            </template>
            <template v-slot:items="job">
              <tr
                :class="{ 'xml-job-list__row--clickable': getMessages(job.item).length }"
                @click="job.expanded = getMessages(job.item).length ? !job.expanded : false"
              >
                <td>
                  <badge
                    v-if="job.item.status === 'NO_CONTENT'"
                    :badge="badges[job.item.status]"
                    :title="$t('xmlChannelStatus.xmlChannelJobList.noJobs')"
                  />
                  <badge v-else :badge="badges[job.item.status]"/>
                </td>
                <td>{{ formatDate(job.item.runStart) }}</td>
                <td>{{ formatDate(job.item.runEnd) }}</td>
                <td>{{ getFormattedDuration(job.item.runStart, job.item.runEnd) }}</td>
                <td>{{ job.item.rows }}</td>
                <td>
                  <v-btn
                    v-if="getMessages(job.item).length"
                    :title="$t('xmlChannelStatus.xmlChannelJobList.showMessages')"
                    small
                    flat
                    icon
                  >
                    <v-icon>{{ job.expanded ? 'expand_less' : 'expand_more' }}</v-icon>
                  </v-btn>
                  <span v-if="getMessages(job.item).length">
                    {{ getMessages(job.item).length }}
                  </span>
                </td>
              </tr>
            </template>
            <template v-slot:expand="job">
              <v-card flat class="xml-job-list__messages">
                <v-card-text>
                  <div
                    v-for="message in getMessages(job.item)"
                    :key="message.key"
                    class="xml-job-list__message"
                  >
                    <div>
                      <strong :class="{ 'error--text': message.error }">{{ message.label }}</strong>
                      <span class="grey--text"> {{ formatDate(message.time) }}</span>
                    </div>
                    <div>{{ message.message }}</div>
                    <pre
                      v-if="message.detail"
                      class="xml-job-list__detail"
                    >{{ message.detail }}</pre>
                  </div>
                </v-card-text>
              </v-card>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            @click="dialog = false"
            color="primary"
            flat
          >
            {{ $t('general.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-flex>
  </v-dialog>
</template>

<style lang="scss">
  .xml-job-list__row--clickable {
    cursor: pointer;
  }

  .xml-job-list__message + .xml-job-list__message {
    margin-top: 12px;
  }

  .xml-job-list__detail {
    max-height: 240px;
    overflow: auto;
    white-space: pre-wrap;
    font-size: 12px;
  }
</style>



// WEBPACK FOOTER //
// src/components/xmlChannelStatus/XmlChannelJobList.vue